/** client/src/App.tsx — Root providers, public routes, and the outer Wouter Switch. */
import { Suspense } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { AnimatePresence, MotionConfig } from "framer-motion";
import { AlertTriangle } from "lucide-react";
import { Redirect, Route, Switch, useLocation } from "wouter";

import { Guarded, NotFound, PageLoader } from "@/app/guards";
import {
  AddBankPage, AddKeyEntry, AuthEntry, BespokeSeeding, BespokeSeedReconfirm,
  ClaimPendingPage, CounterpartyInvitePage, FoundOrgPage, GuardianInvitePage, InviteLandingPage,
  LinkDevicePage, OfficialChannelsPage,
  PathwayStatusPage, PlatformInvitePage, Recovery, Setup, SetupGuardians,
} from "@/app/lazy-pages";
import { ErrorBoundary } from "@/components/error-boundary";
import { AppShellLayout } from "@/components/layout/app-shell";
import { OrgContextProvider } from "@/components/providers/org-context-provider";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { SessionPossessionPrompt } from "@/components/session-possession-prompt";
import { RecoveryFreezeBanner } from "@/components/recovery-freeze-banner";
import { RouteGuard } from "@/components/RouteGuard";
import { Toaster } from "@/components/ui/toast";
import { AUTH_ME_QUERY_KEY, fetchAuthMe } from "@/lib/auth-me";
import { getCspNonce } from "@/lib/csp-nonce";
import { guardianLanding, isGuardianOnlyClientPath } from "@/lib/guardian-only-routing";
import { queryClient } from "@/lib/queryClient";
import { validateReturnTo } from "@/lib/return-to";
import { useCurrentUser } from "@/lib/use-current-user";
import { buildAdminRoutes } from "@/app/admin-routes";
import { buildAppRoutes } from "@/app/app-routes";
import type { CurrentUser } from "@/app/app-routes";
import { CLAIM_PENDING_PATH } from "@/pages/auth-entry/types";
import { buildChallengeRoutes } from "@/app/challenge-routes";
import { FOCUS_PARAM, GOVERNANCE_TAB_PARAM } from "@/pages/dashboard/components/sub-tab-params";
import { ORG_TAB_IDS, PLATFORM_TAB_IDS, type OrgTabId, type PlatformTabId } from "@shared/dashboard-types";
import { PRODUCT_NAME } from "@shared/brand";
import { describeError } from "@/lib/error-copy";

export { VaultIdentifierHistoryRedirect } from "@/app/app-routes";
export type { CurrentUser } from "@/app/app-routes";

// Any ["auth/me"] observer that passes no queryFn of its own (several
// components subscribe naked, relying on a warm cache) fetches via the shared
// bootstrap fetch instead of throwing "No queryFn was passed" once the 30s
// staleTime lapses. Registered here, NOT in lib/queryClient.ts — auth-me.ts
// imports that module, so registering there would create an import cycle.
// NOTE: setQueryDefaults matches by key PREFIX — a future extended key like
// ["auth/me", userId] would inherit this queryFn (which ignores the extra
// segment); give such keys their own explicit queryFn.
queryClient.setQueryDefaults(AUTH_ME_QUERY_KEY, { queryFn: fetchAuthMe });

function validPlatformTab(raw: string | undefined): PlatformTabId {
  return raw !== undefined && (PLATFORM_TAB_IDS as ReadonlyArray<string>).includes(raw)
    ? raw as PlatformTabId : "overview";
}

function validOrgTab(raw: string | undefined): OrgTabId {
  return raw !== undefined && (ORG_TAB_IDS as ReadonlyArray<string>).includes(raw)
    ? raw as OrgTabId : "home";
}

/**
 * Map a retired org tab id (plus its old sub-tab query param) onto its
 * successor route, or canonicalise an unknown id.
 *
 * The sub-tab lives in the query string, so this needs the search string as
 * well as the path segment: `governanceTab=keys` and `governanceTab=approvals`
 * are the two halves of the dissolved Governance tab and land on DIFFERENT
 * tabs. Returns null when `rawTab` is already a live tab id.
 */
export function resolveDashboardTabRedirect(
  rawTab: string | undefined,
  search = "",
): string | null {
  if (rawTab === undefined) return null;
  if ((ORG_TAB_IDS as ReadonlyArray<string>).includes(rawTab)) return null;

  const params = new URLSearchParams(search);
  const focus = params.get(FOCUS_PARAM);
  const suffix = focus ? `?${FOCUS_PARAM}=${encodeURIComponent(focus)}` : "";

  if (rawTab === "overview") return "/dashboard/home";
  if (rawTab === "members") return "/dashboard/people";
  if (rawTab === "governance") {
    // The old tab defaulted to "keys" to preserve the unenrolled-admin nudge,
    // so a bare /dashboard/governance means keys, which now live on People.
    return params.get(GOVERNANCE_TAB_PARAM) === "approvals"
      ? "/dashboard/approvals"
      : `/dashboard/people${suffix}`;
  }
  return "/dashboard/home";
}

/** Resolve a safe post-auth return target, falling back to the AAL landing page. */
export function resolveAuthRedirect(user: CurrentUser, search: string): string {
  // A pending guardian-initiated recovery claim is an interrupt: it wins over
  // returnTo AND guardian-only landing (Task 5b fix round 1, Important 1).
  // This closes the PRF sign-in race where confirmSessionAndPromoteKek seeds
  // ["auth/me"] before AuthEntry's own deferred navigate fires — the /auth
  // route's <Redirect> reads this same resolver off the freshly-seeded cache.
  if (typeof user.pendingRecoveryClaim === "string" && user.pendingRecoveryClaim.length > 0) {
    return `${CLAIM_PENDING_PATH}?claim=${encodeURIComponent(user.pendingRecoveryClaim)}`;
  }
  const returnTo = validateReturnTo(new URLSearchParams(search).get("returnTo"));
  if (user.guardianOnly && (!returnTo || !isGuardianOnlyClientPath(returnTo))) {
    return guardianLanding(user);
  }
  return returnTo ?? guardianLanding(user);
}

/** Render the root redirect from current authentication state. */
export function RootRedirect({ user, loading }: { user: CurrentUser | null; loading: boolean }): JSX.Element {
  if (loading) return <PageLoader label="Checking your sign-in…" />;
  return user ? <Redirect to={guardianLanding(user)} /> : <Redirect to="/auth" />;
}

export function GuardianOnlyPathGuard({ children, loading, user }: {
  children: React.ReactNode;
  loading: boolean;
  user: CurrentUser | null;
}): JSX.Element {
  const [location] = useLocation();
  if (!loading && user?.guardianOnly && !isGuardianOnlyClientPath(location)) {
    return <Redirect to="/guardian" replace />;
  }
  return <>{children}</>;
}

function ClientRouteGuard({ children, loading, user }: {
  children: React.ReactNode;
  loading: boolean;
  user: CurrentUser | null;
}): JSX.Element {
  if (user?.guardianOnly) {
    return <GuardianOnlyPathGuard loading={loading} user={user}>{children}</GuardianOnlyPathGuard>;
  }
  return <RouteGuard>{children}</RouteGuard>;
}

function AppShell(): JSX.Element {
  const { user, isLoading: loading } = useCurrentUser();
  const context = { user: user ?? null, loading };
  const routes = <MotionConfig reducedMotion="user" nonce={getCspNonce()}>
    {/* Possession heartbeat's explanation (ledger #119). Mounted at the shell,
        not per-route: the ceremony is driven from `apiRequest`, so it can fire
        under any screen that performs a gated read, and a per-route mount would
        silently leave the sheet unexplained on whichever route forgot it.
        Renders nothing until a confirmation is actually requested. Deliberately
        OUTSIDE the guarded routes and unconditional on `user` — the 403 that
        triggers it comes from the server, which is the only authority on
        whether the session needs re-proving. */}
    <SessionPossessionPrompt />
    {!user?.guardianOnly && <RecoveryFreezeBanner userId={user?.id} />}
    <Suspense fallback={<PageLoader />}>
      <AnimatePresence mode="wait">
        <ClientRouteGuard {...context}><Switch>
          <Route path="/auth">
            {user && !loading ? <Redirect to={resolveAuthRedirect(user, window.location.search)} /> : <AuthEntry />}
          </Route>
          <Route path="/auth/add-key">
            <Guarded {...context} minAal="AAL2">{user && <AddKeyEntry expectedUserId={user.id} />}</Guarded>
          </Route>
          <Route path="/recovery"><Recovery /></Route>
          <Route path="/recovery/claim-pending">
            <Guarded {...context} minAal="AAL1"><ClaimPendingPage /></Guarded>
          </Route>
          {/* Device linking (#2210, #2280). /link-device is reached by a
              LOGGED-OUT new device, so it must not be Guarded. There is no
              approver route any more: after the direction flip the approval
              lives in Security Centre's add-device dialog, on a device that is
              already signed in. */}
          <Route path="/link-device"><LinkDevicePage /></Route>
          <Route path="/official"><OfficialChannelsPage /></Route>
          <Route path="/i/:token"><InviteLandingPage /></Route>
          <Route path="/bank/add"><Guarded {...context} minAal="AAL2"><AddBankPage /></Guarded></Route>
          <Route path="/onboarding/counterparty-invite/:token"><CounterpartyInvitePage /></Route>
          <Route path="/onboarding/found-org/:inviteId">
            <Guarded {...context} minAal="AAL2"><FoundOrgPage /></Guarded>
          </Route>
          <Route path="/platform-invite/:token"><PlatformInvitePage /></Route>
          <Route path="/onboarding/guardian-invite/:token"><GuardianInvitePage /></Route>
          <Route path="/setup"><Guarded {...context}>{user && <Setup user={user} />}</Guarded></Route>
          <Route path="/setup/guardians"><Guarded {...context} minAal="AAL1"><SetupGuardians /></Guarded></Route>
          <Route path="/bespoke-seeding"><Guarded {...context} minAal="AAL1"><BespokeSeeding /></Guarded></Route>
          {/* Day-2 quick-fire reconfirm screen (Spark Deck redesign, Module 03,
              AC-20) — same Guarded/minAal pattern, inserted adjacent. */}
          <Route path="/bespoke-seed/reconfirm">
            <Guarded {...context} minAal="AAL1"><BespokeSeedReconfirm /></Guarded>
          </Route>
          {/* /onboarding/approve-link/:inviteId and /challenge/:orgId? are
              deliberately NOT mounted: their pages called API routes that have
              no server implementation (/api/onboarding/approve-link|decline-link|
              link-details; /api/challenge/start|result — the latter's backend was
              removed with the legacy tier-challenge draw, ledger #40), so every
              visit was a guaranteed dead end. An honest NotFound beats a page
              that can never work. */}
          <Route path="/pathway-status">
            <Guarded {...context} minAal="AAL2">
              <AppShellLayout displayName={user?.displayName} aal={user?.aal}><PathwayStatusPage /></AppShellLayout>
            </Guarded>
          </Route>
          {buildAppRoutes({ ...context, resolveDashboardTabRedirect, validOrgTab })}
          {buildAdminRoutes({ ...context, validatePlatformTab: (tab) => { void validPlatformTab(tab); } })}
          {buildChallengeRoutes(context)}
          <Route path="/"><RootRedirect {...context} /></Route>
          <Route><NotFound /></Route>
        </Switch></ClientRouteGuard>
      </AnimatePresence>
    </Suspense>
    <Toaster />
  </MotionConfig>;

  return user?.guardianOnly ? routes : <OrgContextProvider>{routes}</OrgContextProvider>;
}

function RootErrorFallback(error: Error, reset: () => void): JSX.Element {
  return <div className="min-h-dvh bg-bg-primary flex items-center justify-center px-4">
    <div className="max-w-sm w-full rounded-[14px] border border-border-default bg-bg-elevated p-6 text-center">
      <AlertTriangle className="mx-auto mb-3 h-7 w-7 text-warning" aria-hidden="true" />
      <p className="text-[14px] font-medium text-text-secondary">
        {describeError(error, { action: `load ${PRODUCT_NAME}`, nextStep: "Reload the page." })}
      </p>
      <p className="mt-1 text-[13px] text-text-tertiary">If it keeps happening, sign out and back in.</p>
      <button type="button" onClick={() => window.location.reload()}
        className="mt-4 inline-flex min-h-[44px] items-center rounded-lg border border-border-default px-4 py-2 text-[13px] text-text-secondary hover:text-text-primary hover:border-interactive/40 transition-colors">
        Reload
      </button>
      <button type="button" onClick={reset}
        className="mt-2 block w-full text-[12px] text-text-tertiary hover:text-text-secondary transition-colors">
        Try again without reloading
      </button>
    </div>
  </div>;
}

export default function App(): JSX.Element {
  return <ErrorBoundary label={PRODUCT_NAME} fallback={RootErrorFallback}>
    <ThemeProvider nonce={getCspNonce()}>
      <QueryClientProvider client={queryClient}>
        <AppShell />
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>;
}
